<template>
  <v-card class="elevation-2 pa-4 ma-4" :loading="processing">
    <v-card-title>{{ $t('Сведения о пользователе') }}</v-card-title>
    <v-form ref="form" :readonly="!editing">
      <Input
        label="Логин"
        placeholder="Введите логин"
        v-model="user.username"
        :error-messages="errors['username']"
        :rules="[].concat(validation.required(), validation.text(255))"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
        @blur="checkExistsUser($event.target.value, 'username')"
        @input="errors['username'] = null"
      />
      <v-checkbox
        v-if="libUser.isETPAdmin(currentUser.role.code)"
        class="checkBoxAdmin"
        :label="$t('Администратор ЭТП')"
        v-model="checkIsAdminEtp"
        :disabled="!editing || processing"
        true-icon="mdi-checkbox-outline"
        false-icon="mdi-checkbox-blank-outline"
      />
      <Input
        label="Пароль"
        placeholder="Введите пароль"
        :hint="editing && userId ? $t('Для сохранения старого пароля оставьте поле пустым') : ''"
        type="password"
        v-model="user.password"
        :rules="getPasswordRules()"
        :hide-details="isActivePasswordPolicy"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
        :required="!userId"
      />
      <div style="font-size: 14px" v-if="isActivePasswordPolicy">
        <span v-for="rule in passwordRules" :class="rule.class">{{rule.text}}</span>
      </div>
      <Input
        label="Фамилия"
        placeholder="Введите фамилию"
        v-model="user.lastName"
        :rules="[].concat(validation.required(), validation.text(255))"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
        :required="editing"
      />
      <Input
        label="Имя"
        placeholder="Введите имя"
        v-model="user.firstName"
        :rules="[].concat(validation.required(), validation.text(255))"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
        :required="editing"
      />
      <Input
        label="Отчество"
        placeholder="Введите отчество"
        v-model="user.middleName"
        :rules="[].concat(validation.text(255))"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
      />
      <Input
        label="Email"
        placeholder="Введите Email"
        v-model="user.email"
        :error-messages="errors['email']"
        :rules="[].concat(validation.required(), validation.text(255), validation.email())"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
        @blur="checkExistsUser($event.target.value, 'email')"
        @input="errors['email'] = null"
      />
      <Input
        label="Телефон"
        placeholder="Введите номер телефона"
        v-model="user.phone"
        :rules="[].concat(validation.required(), validation.phone())"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
      />
      <AutoCompleteInput
        v-if="!checkIsAdminEtp && libUser.isETPAdmin(currentUser.role.code)"
        label="Организация"
        v-model="user.contragentId"
        no-data-text="Отсутствуют данные"
        no-filter
        :loading="contragent.loading"
        :rules="validation.required()"
        :items="!!contragentSearch || userId ? contragent.items : []"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
        class="mb-6"
        @update:search="handleSearchContragent"
      />
      <SelectInput
        v-if="!checkIsAdminEtp && user.contragentId"
        label="Роль пользователя"
        no-data-text="Отсутствуют данные"
        v-model="user.role.id"
        :items="roles"
        item-value="id"
        item-title="name"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
        class="mb-6"
      />
      <SelectInput
        label="Язык"
        no-data-text="Отсутствуют данные"
        v-model="user.lang"
        :items="langs"
        item-value="id"
        item-title="name"
        :disabled="processing"
        :class="{ 'disabled': !editing }"
      />
    </v-form>
    <v-card-actions class="mt-6" style="float: right">
      <v-btn
        color="primary"
        density="comfortable"
        variant="outlined"
        :disabled="processing"
        @click="toPreviosPage()"
      >{{ $t('Назад') }}</v-btn>
      <v-btn
        v-if="!editing && !btnHidden"
        color="primary"
        density="comfortable"
        variant="elevated"
        :disabled="processing"
        @click="editAction()"
      >{{ $t('Редактировать') }}</v-btn>
      <v-btn
        v-else-if="editing && !btnHidden"
        color="primary"
        density="comfortable"
        variant="elevated"
        :disabled="processing"
        @click="saveAction()"
      >{{ $t('Сохранить') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import api from '@/api';
import { toast } from '@/lib/toast';
import Input from "@intec/vue-components/form/Input.vue";
import SelectInput from '@intec/vue-components/form/SelectInput.vue';
import AutoCompleteInput from "@intec/vue-components/form/AutoCompleteInput.vue";
import Config from "@/config";
import {useHardPasswordRules} from "@/lib/composable";
import validation from '@/components/validation';
import { isETPAdmin } from '@/lib/user';
import { LANGS } from '@/lib/lang';

export default {
  name: 'User',
  components: {Input, SelectInput, AutoCompleteInput},
  setup() {
    const isActivePasswordPolicy = Config.getConfig('PASSWORD_POLICY')?.value;
    const {validatePassword, passwordRules} = useHardPasswordRules();
    return {validatePassword, passwordRules, isActivePasswordPolicy}
  },
  props: {
    userId: {
      type: String
    }
  },
  data() {
    return {
      editing: false,
      processing: false,
      btnHidden: false,
      errors: {
        'username': null,
        'email': null
      },
      currentUser: Config.getUser().opts,
      user: {
        username: null,
        lastName: null,
        firstName: null,
        middleName: null,
        email: null,
        phone: null,
        lang: 'en',
        role: {
          id: null,
          name: null
        },
        contragentId: null
      },
      libUser: {
        isETPAdmin: isETPAdmin
      },
      contragent: {
        loading: false,
        items: [],
      },
      roles: [],
      langs: LANGS,
      checkIsAdminEtp: false,
      contragentSearch: '',
      validation,
    };
  },
  watch: {
    'user.contragentId': {
      handler(newVal) {
        if (!this.checkIsAdminEtp && newVal) {
          this.loadRoles(newVal);
        }
      }
    }
  },
  methods: {
    getPasswordRules() {
      const baseRules = !this.userId ? [validation.required()] : [];

      if (this.isActivePasswordPolicy) {
        return [].concat(this.validatePassword(false)).flat();
      } else {
        return baseRules.concat(validation.text(255)).flat();
      }
    },
    async load() {
      this.processing = true;
      try {
        const user = await api.get(`/admin/users/${this.userId}/load`);
        this.user = user;
        if (user.contragent) {
          this.contragent.items = [
            {
              value: user.contragentId,
              title: user.contragent.fullName,
            },
          ];
        }
        this.checkIsAdminEtp = this.user.role.id === 1;
      } catch (e) {
        toast.error(e.message);
      }
      this.processing = false;
    },
    async loadRoles(contragentId) {
      this.processing = true;
      try {
        const { defaultRole, roles } = await api.get(`/admin/users/roles`, { contragentId: contragentId });
        this.roles = roles;
        if (!this.userId) {
          this.user.role = defaultRole;
        }
      } catch (e) {
        toast.error(e.message);
      }
      this.processing = false;
    },
    async saveAction() {
      this.processing = true;
      const hasErrors = Object.values(this.errors).some(value => value !== null);
      if ((await this.$refs.form.validate()).valid && !hasErrors) {
        try {
          let userData = JSON.parse(JSON.stringify(this.user));
          if (this.checkIsAdminEtp) {
            userData.role.id = 1; //Админ ЭТП
            delete userData.contragentId;
          }
          let formData = new FormData();

          formData.append('data', JSON.stringify(userData));
          if (this.userId) {
            await api.post(`/admin/users/${this.userId}/save`, formData);
            toast.info('Сведения о пользователе успешно сохранены');
            if (this.userId == this.currentUser.id) {
              location.reload();
            }
          } else {
            await api.post(`/admin/users/create`, formData);
            this.$router.replace({
              name: 'users.all'
            });
            toast.info('Сведения о пользователе успешно сохранены');
            history.back();
          }
        } catch (e) {
          toast.error(e.message);
        }
      } else {
        toast.error('Проверьте правильность заполнения формы');
      }
      this.processing = false;
    },
    async checkExistsUser(value, type) {
      if (!this.editing) {
        return;
      }

      try {
        let formData = new FormData();
        formData.append('data', JSON.stringify({ userId: this.userId ?? null, value, type }));
        await api.post('/auth/checkIfExists', formData);
        this.errors[type] = null;
      } catch (e) {
        this.errors[type] = e.message;
      }
    },
    async queryContragentSelections(v) {
      if (this.contragent.loading) {
        return;
      }
      let query = {
        search: v,
        filter: null,
        range: {},
        sort: [{ column: 'c.fullName', order: 'ASC' }],
      };

      this.contragent.loading = true;
      let response = await api.get('/admin/contragents', {
        query: JSON.stringify(query),
      });
      let items = [];
      response.entries.forEach(function (item) {
        items.push({ value: item.c.id, title: item.c.fullName, isOrganizer: item.c.isOrganizer });
      });
      this.contragent.items = items;
      this.contragent.loading = false;
    },
    handleSearchContragent(val) {
      this.contragentSearch = val;

      if (val && val !== this.user.contragentId) {
        this.queryContragentSelections(val);
      }
    },
    editAction() {
      this.editing = true;
      this.$refs.form.validate();
    },
    toPreviosPage() {
      history.back();
    }
  },
  created() {
    this.editing = ['edit', 'create'].some(route => this.$route.path.includes(route));
    this.btnHidden = this.$route.path.includes('view');
    if (this.currentUser.contragent) {
      this.user.contragentId = this.currentUser.contragent.id;
    }
    if (this.userId) {
      this.load();
    }
  }
};
</script>
<style scoped lang="scss">
:deep(.checkBoxAdmin .v-selection-control) {
  min-height: fit-content;
}

:deep(.checkBoxAdmin .v-label) {
  font-family: 'Abrade';
  font-weight: 500;
  font-size: 16px;
  opacity: 1;
}
</style>
